import {
  BottomBar,
  Form,
  Grid,
  ISelectMultiOption,
  ISelectOption,
  MainArea,
  Page,
  Panel,
  SaveBar,
  Select,
  SelectMulti,
  SingleColumnLayout,
  TopArea,
  P,
  Gap,
  Input,
  Button,
} from '@salecycle/ui-component-library';
import AppTopBar from 'core/ui/components/AppTopBar/AppTopBar';
import Spinner from 'core/ui/components/spinner';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  fullNameValidCheck,
  getAccessLevelOptions,
  getRoleOptions,
  isAccessLevelSaleCycle,
} from 'sections/user-management/utils/helpers';
import { UnsavedChangesModal } from 'core/modals/UnsavedChangesModal';
import useNotifications from 'core/notifications/useNotifications';
import { useClients, useFeatureFlags } from '../../../../core/app/AppContext';
import { getSelectOptionsForMultiSites } from '../../../../components/utils/getSelectOptionsForMultiSites';
import { Brand, Group, Site } from '../../../client-management/entities';
import { IUserData, Level, Role } from '../../entities';
import { useMutation, useQuery } from 'react-query';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import { useTranslation } from 'react-i18next';
import { entityToSortedSelectOptions } from 'components/utils/getSelectOptions';

export function EditUserPage() {
  const { t } = useTranslation();
  const { push } = useHistory();
  const { sub } = useParams<{ sub: string }>();
  const { showNotification } = useNotifications();
  const { getSites, getBrands, getGroups } = useClients();
  const { flagEnabled } = useFeatureFlags();

  const rolesQuery = useQuery('roles', async () => {
    const result = await RestApi.get<Role[]>(`/roles`);
    return result.data;
  });

  const userQuery = useQuery(['get-user', sub], async () => {
    const result = await RestApi.get<IUserData>(`/users/${sub}`, { sub });
    return result.data;
  });

  const resetPasswordQuery = async () => {
    const result = await RestApi.get<string>(`/users/${sub}/resend_invite`);
    return result.data;
  };

  const user: IUserData = userQuery?.data ?? null;

  const [unsavedChangesModalIsOpen, setUnsavedChangesModalIsOpen] = React.useState(false);
  const [fullName, setFullName] = React.useState(null);
  const [fullNameValid, setFullNameValid] = React.useState(false);
  const [selectedRole, setSelectedRole] = React.useState(null);
  const [selectedAccessLevel, setSelectedAccessLevel] = React.useState(null);
  const [selectedBrand, setSelectedBrand] = React.useState(null);
  const [selectedSite, setSelectedSite] = React.useState(null);
  const [selectedMultiSiteGroup, setSelectedMultiSiteGroup] = React.useState(null);
  const [selectedMultiSites, setSelectedMultiSites] = React.useState(null);
  const accessLevelOptions = React.useMemo(() => getAccessLevelOptions(t), [t]);
  // Temporary fix to resolve a security issue. Will be replaced by mapping between access levels and roles later
  const [accessLevelIsSaleCycle, setAccessLevelIsSaleCycle] = React.useState(false);
  const roleOptions = React.useMemo(() => getRoleOptions(rolesQuery?.data ?? [], accessLevelIsSaleCycle), [
    rolesQuery.data,
    accessLevelIsSaleCycle,
  ]);
  const groupOptions = React.useMemo(() => entityToSortedSelectOptions<Group>(getGroups()), [getGroups]);
  const brandOptions = React.useMemo(() => entityToSortedSelectOptions<Brand>(getBrands()), [getBrands]);
  const siteOptions = React.useMemo(() => entityToSortedSelectOptions<Site>(getSites()), [getSites]);
  const multiSiteOptions = React.useMemo(
    () => getSelectOptionsForMultiSites(selectedMultiSiteGroup?.value, getSites(), getBrands()),
    [selectedMultiSiteGroup, getSites, getBrands],
  );

  //Set selected role once roles have loaded
  React.useEffect(() => {
    if (!rolesQuery.isLoading) {
      setSelectedRole(roleOptions.find((r: ISelectOption<string>) => r.value === user?.role));
    }
  }, [user, rolesQuery.isLoading, roleOptions]);

  //Populate all other fields once the user has loaded
  React.useEffect(() => {
    if (!userQuery.isLoading) {
      setFullName(user?.name);
      setSelectedAccessLevel(accessLevelOptions.find(a => a.value === user?.accessLevel));
      setAccessLevelIsSaleCycle(isAccessLevelSaleCycle(user?.accessLevel));
      setSelectedBrand(brandOptions.find(b => b.value === (user?.brandIds?.[0] ?? null)));
      setSelectedSite(siteOptions.find(s => s.value === (user?.siteIds?.[0] ?? null)));
      setSelectedMultiSiteGroup(groupOptions.find(g => g.value === (user?.groupIds?.[0] ?? null)));
      setSelectedMultiSites(siteOptions.filter(s => user?.siteIds?.find(id => s.value === id)));
    }
  }, [userQuery.isLoading, accessLevelOptions, brandOptions, siteOptions, groupOptions, user]);

  //Whenever full name changes check its validity
  React.useEffect(() => {
    setFullNameValid(fullNameValidCheck(fullName));
  }, [fullName]);

  const gotoUserManagementPage = () => {
    setUnsavedChangesModalIsOpen(false);
    push('/user-management/users');
  };

  const putUserMutation = useMutation((user: IUserData) => RestApi.put(`/users/${user.sub}`, user));
  const handleSaveUser = async () => {
    const accessLevel = selectedAccessLevel.value;
    const role = selectedRole.value;
    let groupIds = [];
    let brandIds = [];
    let siteIds = [];

    switch (accessLevel) {
      case 'Brand': {
        brandIds = selectedBrand ? [selectedBrand.value] : [];
        break;
      }
      case 'Site': {
        siteIds = selectedSite ? [selectedSite.value] : [];
        break;
      }
      case 'MultiSite': {
        siteIds = selectedMultiSites ? selectedMultiSites.map(s => s.value) : [];
        groupIds = selectedMultiSiteGroup ? [selectedMultiSiteGroup.value] : [];
      }
    }

    const cloneUserData = { ...user };
    // remove user status before submission
    delete cloneUserData.userStatus;

    const updatedUser = {
      ...cloneUserData,
      name: fullName,
      accessLevel,
      role,
      brandIds,
      siteIds,
      groupIds,
    };
    putUserMutation.mutate(updatedUser, {
      onSuccess: () => {
        showNotification(
          t('editUser.saveUser.notification.success', {
            name: user.name,
          }),
        );
        gotoUserManagementPage();
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(
          t('editUser.saveUser.notification.error', {
            error: error.response.data.errors.join(),
          }),
          'error',
        );
      },
    });
  };

  const handleDiscardButtonClicked = () => {
    setFullName(user?.name);
    setSelectedAccessLevel(accessLevelOptions.find(a => a.value === user?.accessLevel));
    setAccessLevelIsSaleCycle(isAccessLevelSaleCycle(user?.accessLevel));
    setSelectedBrand(brandOptions.find(b => b.value === (user?.brandIds?.[0] ?? null)));
    setSelectedSite(siteOptions.find(s => s.value === (user?.siteIds?.[0] ?? null)));
    setSelectedMultiSiteGroup(groupOptions.find(g => g.value === (user?.groupIds?.[0] ?? null)));
    setSelectedMultiSites(siteOptions.filter(s => user?.siteIds?.find(id => s.value === id)));
    setSelectedRole(roleOptions.find(r => r.value === user?.role));
  };

  const handleResetPasswordButtonClicked = () => {
    resetPasswordQuery()
      .then(() => {
        showNotification(t('editUser.resendInvite.notification.success'));
      })
      .catch((error: Error): void => {
        showNotification(t('editUser.resendInvite.notification.error') + error.message, 'error');
      });
  };

  const isUserStatusEqualTo = (userStatus: String) => {
    return user.userStatus === userStatus;
  };

  const isAccessLevelValid = () => {
    let valid = false;
    if (selectedAccessLevel) {
      switch (selectedAccessLevel.value) {
        case 'SaleCycle': {
          valid = true;
          break;
        }
        case 'Brand': {
          valid = selectedBrand != null;
          break;
        }
        case 'Site': {
          valid = selectedSite != null;
          break;
        }
        case 'MultiSite': {
          valid = selectedMultiSiteGroup != null && selectedMultiSites?.length > 0;
          break;
        }
      }
    }
    return valid;
  };

  if (rolesQuery.isLoading || userQuery.isLoading) {
    return <Spinner />;
  }

  const getUserStatus = (): string => {
    return t(`editUser.userStatus.${user?.userStatus}`);
  };

  return (
    <MainArea>
      <TopArea>
        <AppTopBar />
      </TopArea>

      <Page padding={0}>
        <>
          <UnsavedChangesModal
            heading={t('editUser.unsavedChangesModal.heading')}
            isOpen={unsavedChangesModalIsOpen}
            onOkButtonClicked={gotoUserManagementPage}
            onCancelButtonClicked={() => setUnsavedChangesModalIsOpen(false)}
          >
            <P>{t('editUser.unsavedChangesModal.text1')}</P>
            <Gap top={10} />
            <P>{t('editUser.unsavedChangesModal.text2')}</P>
          </UnsavedChangesModal>
          <SingleColumnLayout paddingLeft={20} heading={user?.name}>
            <Grid>
              <Panel padding={0} variation="clean">
                <Form>
                  <Gap top={30} />
                  <h4>{t('editUser.personalInfo.heading')}</h4>
                  <Gap top={30} />
                  <Input
                    label={t('editUser.fullName.label')}
                    ariaLabel={t('editUser.fullName.label')}
                    inputId="FullNameInput"
                    placeholder={t('editUser.fullName.placeholder')}
                    validationMessage={!fullNameValid ? t('editUser.fullName.validation') : null}
                    value={fullName}
                    onChange={value => setFullName(value)}
                  />
                  <Input
                    disabled={true}
                    label={t('editUser.email.label')}
                    ariaLabel={t('editUser.email.label')}
                    inputId="EmailInput"
                    type="email"
                    placeholder={t('newUser.addUserStep.email.placeholder')}
                    value={user?.email}
                  />
                  {flagEnabled('edit-user-status') && user?.userStatus && (
                    <Input
                      disabled={true}
                      label={t('editUser.userStatus.label')}
                      ariaLabel={t('editUser.userStatus.label')}
                      value={getUserStatus()}
                    />
                  )}
                  <Gap top={30} />
                  <h4>{t('editUser.accessInfo.heading')}</h4>
                  <Gap top={30} />
                  <Select
                    ariaLabel={t('editUser.accessLevel.label')}
                    label={t('editUser.accessLevel.label')}
                    inputId="AccessLevelSelect"
                    onChange={(selectedOption: ISelectOption<string>) => {
                      setSelectedAccessLevel(selectedOption);
                      setAccessLevelIsSaleCycle(isAccessLevelSaleCycle(selectedOption.value as Level));
                    }}
                    options={accessLevelOptions}
                    value={selectedAccessLevel}
                    clearable={true}
                  />

                  {selectedAccessLevel && selectedAccessLevel.value === 'Brand' && (
                    <Select
                      ariaLabel={t('editUser.levels.brand')}
                      label={t('editUser.levels.brand')}
                      inputId="BrandSelect"
                      onChange={(selectedOption: ISelectOption<string>) => setSelectedBrand(selectedOption)}
                      options={brandOptions}
                      value={selectedBrand}
                      clearable={true}
                      minLength={2}
                      minLengthPlaceholder={t('common.minLengthPlaceholder', {
                        minLength: 2,
                      })}
                      noOptionsPlaceholder={t('common.noOptionsPlaceholder')}
                    />
                  )}
                  {selectedAccessLevel && selectedAccessLevel.value === 'Site' && (
                    <Select
                      ariaLabel={t('editUser.levels.site')}
                      label={t('editUser.levels.site')}
                      inputId="SiteSelect"
                      onChange={(selectedOption: ISelectOption<string>) => setSelectedSite(selectedOption)}
                      options={siteOptions}
                      value={selectedSite}
                      clearable={true}
                      minLength={2}
                      minLengthPlaceholder={t('common.minLengthPlaceholder', {
                        minLength: 2,
                      })}
                      noOptionsPlaceholder={t('common.noOptionsPlaceholder')}
                    />
                  )}
                  {selectedAccessLevel && selectedAccessLevel.value === 'MultiSite' && (
                    <>
                      <Select
                        ariaLabel={t('editUser.levels.group')}
                        label={t('editUser.levels.group')}
                        inputId="GroupSelect"
                        onChange={(selectedOption: ISelectOption<string>) => {
                          setSelectedMultiSiteGroup(selectedOption);
                          setSelectedMultiSites(null);
                        }}
                        options={groupOptions}
                        value={selectedMultiSiteGroup}
                        clearable={true}
                        minLength={2}
                        minLengthPlaceholder={t('common.minLengthPlaceholder', {
                          minLength: 2,
                        })}
                        noOptionsPlaceholder={t('common.noOptionsPlaceholder')}
                      />

                      {selectedMultiSiteGroup && (
                        <SelectMulti
                          ariaLabel={t('editUser.levels.site_plural')}
                          label={t('editUser.levels.site_plural')}
                          inputId="MultiSiteSelect"
                          onChange={(selectedOptions: ISelectMultiOption<string>[]) =>
                            setSelectedMultiSites(selectedOptions)
                          }
                          options={multiSiteOptions}
                          value={selectedMultiSites}
                          clearable={true}
                          noOptionsPlaceholder={t('common.noOptionsPlaceholder')}
                        />
                      )}
                    </>
                  )}
                  <Select
                    ariaLabel={t('editUser.role.label')}
                    label={t('editUser.role.label')}
                    inputId="RoleSelect"
                    onChange={(selectedOption: ISelectOption<string>) => setSelectedRole(selectedOption)}
                    options={roleOptions}
                    value={selectedRole}
                    clearable={true}
                    isSorted={true}
                  />
                  <Gap top={30} />
                  {isUserStatusEqualTo('FORCE_CHANGE_PASSWORD') && <h4>{t('editUser.accountInfo.heading')}</h4>}
                  <Gap top={30} />
                </Form>
                {isUserStatusEqualTo('FORCE_CHANGE_PASSWORD') && (
                  <Button variation="secondary" onClick={handleResetPasswordButtonClicked}>
                    {t('editUser.resendInvite.buttonLabel')}
                  </Button>
                )}
              </Panel>
            </Grid>
          </SingleColumnLayout>
        </>
      </Page>
      <BottomBar>
        <SaveBar
          onSaveButtonClicked={handleSaveUser}
          onExitButtonClicked={() => setUnsavedChangesModalIsOpen(true)}
          onDiscardButtonClicked={handleDiscardButtonClicked}
          hasValidationFailed={!fullNameValid || !isAccessLevelValid() || selectedRole?.value == null}
          enableSaveButton={true}
        />
      </BottomBar>
    </MainArea>
  );
}
